import React, { useState, useEffect } from 'react';
import { X, FileText, Edit2, Trash2, Plus, Eye } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const TermsConditionForm = ({ 
  mode = 'new', 
  termsCondition = null, 
  availablePrograms = [], 
  onClose, 
  onSubmit, 
  onEdit, 
  onView, 
  onDelete, 
  termsConditions = [] 
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [program, setProgram] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    try {
      if (mode === 'edit' || mode === 'view') {
        setTitle(termsCondition?.title || '');
        setContent(termsCondition?.content || '');
        setProgram(termsCondition?.program?._id || '');
      } else {
        setTitle('');
        setContent('');
        setProgram('');
      }
      setErrors({});
      setShowSuccess(false);
    } finally {
      setIsLoading(false);
    }
  }, [mode, termsCondition]);


  const isList = mode === 'list';
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isNew = mode === 'new';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!content.trim()) {
      newErrors.content = 'Content is required';
    } else {
      const plainText = content.replace(/<[^>]*>/g, '').trim();
      if (plainText.length < 10) {
        newErrors.content = 'Content must contain at least 10 characters of text';
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setIsSubmitting(true);
    
    try {
      await onSubmit({
        title: title.trim(),
        content,
        program: program || null
      });
      
      setShowSuccess(true);
      setTimeout(() => {
        if (isNew) {
          setTitle('');
          setContent('');
          setProgram('');
          setShowSuccess(false);
        }
        onClose();
      }, 1500);
    } catch (error) {
      setErrors({ submit: 'Failed to save Terms & Conditions. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'link', 'align'
  ];

  if (isList) {
    return (
      <div className="mt-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h2 className="text-2xl font-bold text-[#054581]">Terms & Conditions Management</h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <button
              className="bg-[#1086a4] text-white px-4 sm:px-6 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center justify-center"
              onClick={() => onEdit(null)}
            >
              <Plus className="w-5 h-5 mr-2" />
              Add New Terms & Conditions
            </button>
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden space-y-4">
          {termsConditions.map(tc => (
            <div key={tc._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#1086a4] bg-opacity-10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-[#1086a4]" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#054581] mb-2">{tc.title}</h3>
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600">
                        {tc.program?.title ? (
                          <span className="text-[#1e2d40]">Program: {tc.program.title}</span>
                        ) : (
                          <span className="text-gray-500">No program assigned</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {tc.contentPreview || 
                         (tc.content ? tc.content.replace(/<[^>]*>/g, '').trim().substring(0, 120) + '...' : 'No content')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={() => onView(tc)}
                  className="p-2 text-[#1086a4] hover:text-[#054581] transition-colors rounded-lg hover:bg-white"
                >
                  <Eye size={18} />
                </button>
                <button
                  onClick={() => onEdit(tc)}
                  className="p-2 text-[#1086a4] hover:text-[#054581] transition-colors rounded-lg hover:bg-white"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => onDelete(tc)}
                  className="p-2 text-red-500 hover:text-red-700 transition-colors rounded-lg hover:bg-white"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block w-full overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Title
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Program
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider">
                  Content Preview
                </th>
                <th scope="col" className="relative px-6 py-3 w-24">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {termsConditions.map(tc => (
                <tr key={tc._id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{tc.title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {tc.program?.title || <span className="text-gray-500">No program</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {tc.contentPreview || 
                       (tc.content ? tc.content.replace(/<[^>]*>/g, '').trim().substring(0, 120) + '...' : 'No content') ||
                       'No content'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => onView(tc)}
                        className="text-[#1086a4] hover:text-[#054581] transition-colors"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onEdit(tc)}
                        className="text-[#1086a4] hover:text-[#054581] transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(tc)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <form
        className="bg-white rounded-lg shadow-lg w-full relative border-2 border-[#1086a4] max-h-[90vh] overflow-y-auto sm:max-w-lg md:max-w-4xl"
        onSubmit={handleSubmit}
      >
        {/* Header */}
        <div className="p-6 border-b border-[#1086a4]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#054581]">
              {isNew && 'Add New Terms & Conditions'}
              {isEdit && 'Edit Terms & Conditions'}
              {isView && 'View Terms & Conditions'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
              disabled={isSubmitting}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className="p-4 bg-green-50 border-b border-green-200">
            <p className="text-green-700 text-sm font-medium">
              Terms & Conditions {isNew ? 'created' : 'updated'} successfully!
            </p>
          </div>
        )}

        {/* Error Message */}
        {errors.submit && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <p className="text-red-700 text-sm font-medium">{errors.submit}</p>
          </div>
        )}

        {/* Form Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1086a4]"></div>
            </div>
          ) : (
            <>
              {/* Title Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 placeholder-gray-500 ${
                    errors.title 
                      ? 'border-red-500 focus:border-red-400' 
                      : 'border-[#1086a4] focus:border-[#1086a4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  required
                  disabled={isView || isSubmitting}
                  placeholder="Enter terms and conditions title"
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>

              {/* Program Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  Program (Optional)
                </label>
                <select
                  className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                  value={program}
                  onChange={e => setProgram(e.target.value)}
                  disabled={isView || isSubmitting}
                >
                  <option value="">Select a program (optional)</option>
                  {Array.isArray(availablePrograms) && availablePrograms.map(prog => (
                    <option key={prog._id} value={prog._id}>
                      {prog.title}
                    </option>
                  ))}
                  {!Array.isArray(availablePrograms) && (
                    <option disabled>Loading programs...</option>
                  )}
                </select>
              </div>

              {/* Content Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  Content <span className="text-red-500">*</span>
                </label>
                <div className={`border rounded-md ${errors.content ? 'border-red-500' : 'border-[#1086a4]'}`}>
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={modules}
                    formats={formats}
                    placeholder="Enter the terms and conditions content..."
                    readOnly={isView}
                    className="bg-[#f9fafb]"
                    style={{
                      height: '300px',
                      marginBottom: '42px'
                    }}
                  />
                </div>
                {errors.content && (
                  <p className="text-red-500 text-sm mt-1">{errors.content}</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {(isNew || isEdit) && (
          <div className="border-t border-[#1086a4] p-6">
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-2 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isNew ? 'Adding...' : 'Saving...'}
                  </>
                ) : (
                  isNew ? 'Add Terms & Conditions' : 'Save Changes'
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default TermsConditionForm;